/**
 * Sheet de priming de permissões de dispositivo (câmera/microfone), pt-BR.
 *
 * Explica **por que** o app precisa do recurso antes do prompt do SO (modo "priming")
 * e orienta às Configurações do dispositivo quando o acesso está negado (modo "blocked").
 * Espelha a linguagem do fluxo de notificações. Reutiliza `Card`, `Button` e os ícones
 * do DS Synap. Um único CTA primário; scrim; pilha única de overlays (`useOverlay`: voltar
 * web/nativo, Esc, foco preso); rota de escape (voltar/Esc/scrim);
 * respeita `prefers-reduced-motion`; alvos de toque ≥44px (via `Button`).
 */

import { useId, useRef } from 'react'
import { useOverlay } from '@/shared/lib/overlay/useOverlay'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { CameraIcon, MicIcon, SettingsIcon } from '@/shared/ui/icons'
import { platform } from '@/shared/lib/platform/platform'
import type { DeviceKind } from '@/shared/lib/permissions/useDevicePermissionRequest'

export interface PermissionPrimingSheetProps {
  open: boolean
  kind: DeviceKind
  /** Racional pt-BR: por que o app precisa do recurso. */
  reason: string
  /** `priming` = pedir pela 1ª vez; `blocked` = negado, orientar Configurações. */
  mode: 'priming' | 'blocked'
  /** Solicitação em andamento: desabilita as ações. */
  busy?: boolean
  /** CTA primário do modo priming ("Permitir"). */
  onConfirm: () => void
  /** Secundário / scrim / Esc ("Agora não" ou "Fechar"). */
  onDismiss: () => void
  /** CTA primário do modo blocked ("Abrir Configurações"). */
  onOpenSettings?: () => void
}

const KIND_LABEL: Record<DeviceKind, string> = {
  camera: 'câmera',
  microphone: 'microfone',
}

function KindIcon({ kind }: { kind: DeviceKind }) {
  const Icon = kind === 'camera' ? CameraIcon : MicIcon
  return <Icon size={26} className="text-[var(--color-teal-ink)]" aria-hidden="true" />
}

export const PermissionPrimingSheet = ({
  open,
  kind,
  reason,
  mode,
  busy = false,
  onConfirm,
  onDismiss,
  onOpenSettings,
}: PermissionPrimingSheetProps) => {
  const titleId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const primaryRef = useRef<HTMLButtonElement | null>(null)

  // Voltar (web e nativo) e Esc = «Agora não»; foco no CTA primário, preso no painel e devolvido
  // ao gatilho ao fechar.
  useOverlay(open, {
    kind: 'sheet',
    label: 'Permissão de dispositivo',
    panelRef,
    initialFocusRef: primaryRef,
    onRequestClose: () => onDismiss(),
  })

  if (!open) return null

  const label = KIND_LABEL[kind]
  const isBlocked = mode === 'blocked'
  const isNative = platform.isNative()

  const title = isBlocked
    ? `Acesso à ${label} bloqueado`
    : kind === 'camera'
      ? 'Permitir acesso à câmera'
      : 'Permitir acesso ao microfone'

  const settingsHint = isNative
    ? `Abra as Configurações do dispositivo → Apps → Synap → Permissões e ative a ${label}.`
    : `Toque no ícone de cadeado (ou i) na barra de endereço → Permissões → ${
        kind === 'camera' ? 'Câmera' : 'Microfone'
      } → Permitir.`

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onDismiss()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="w-full max-w-[440px]"
      >
        <Card className="space-y-4 rounded-[var(--radius)] p-5 shadow-[0_18px_45px_rgba(31,43,40,0.22)]">
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-mint-soft)]">
              <KindIcon kind={kind} />
            </span>
            <h2 id={titleId} className="text-base font-bold text-[var(--color-text-primary)]">
              {title}
            </h2>
          </div>

          <div id={descriptionId} className="space-y-2 text-sm text-[var(--color-text-secondary)]">
            <p>{reason}</p>
            {isBlocked ? (
              <p className="text-[var(--color-text-secondary)]">
                Você negou o acesso anteriormente. {settingsHint}
              </p>
            ) : (
              <p className="text-xs text-[var(--color-text-muted)]">
                Em seguida, seu dispositivo pedirá a confirmação. Você pode revogar quando quiser.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row-reverse">
            {isBlocked ? (
              <>
                <Button
                  ref={primaryRef}
                  variant="primary"
                  fullWidth
                  disabled={busy}
                  onClick={() => onOpenSettings?.()}
                  className="sm:flex-1"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <SettingsIcon size={18} aria-hidden="true" />
                    Abrir Configurações
                  </span>
                </Button>
                <Button variant="ghost" fullWidth onClick={onDismiss} className="sm:flex-1">
                  Fechar
                </Button>
              </>
            ) : (
              <>
                <Button
                  ref={primaryRef}
                  variant="primary"
                  fullWidth
                  disabled={busy}
                  onClick={onConfirm}
                  className="sm:flex-1"
                >
                  {busy ? 'Solicitando…' : 'Permitir'}
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  disabled={busy}
                  onClick={onDismiss}
                  className="sm:flex-1"
                >
                  Agora não
                </Button>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}